/**
 * The only module that talks to `search-insights`.
 *
 * Consent is **injected** rather than read here: the package stays free of any
 * Iubenda dependency, and each app wires the gate its own way. Nothing is sent
 * — and `search-insights` is never initialised — until the host says it may be.
 */
import aa from 'search-insights';
import { clearInsightContext } from './context';
/** localStorage key holding the authenticated user token. */
const USER_TOKEN_KEY = 'algolia_user_token';
let state = null;
let initialised = false;
/**
 * Initialises `search-insights` and stores the app's configuration.
 *
 * Idempotent: the underlying `aa('init')` runs once, on the first call. Later
 * calls only refresh the consent gate and cookie domain, so a consent change
 * does not reconfigure the library.
 *
 * @param options - Credentials, cookie domain and the consent gate.
 */
export function initAlgoliaInsights(options) {
    if (typeof window === 'undefined')
        return;
    state = {
        cookieDomain: options.cookieDomain,
        coursesIndex: options.coursesIndex,
        isAllowed: options.isAllowed
    };
    if (!options.cookieDomain) {
        // Without a parent-domain cookie the context never reaches the checkout
        // subdomain, so every order silently stops being attributed — and with a
        // list of entries a host-only cookie also masks the real one on read.
        // Worth a loud line: it is the one misconfiguration that costs the most
        // and shows no other symptom.
        console.warn('[algolia-insights] no cookie domain configured: the attribution context will not cross subdomains and order conversions will not be attributed.');
    }
    if (initialised)
        return;
    try {
        aa('init', {
            appId: options.appId,
            apiKey: options.apiKey,
            // Anonymous user token, persisted by search-insights in the _ALGOLIA cookie.
            useCookie: true,
            // Do not clobber a configuration InstantSearch's insights middleware may
            // have already installed.
            partial: true
        });
        initialised = true;
        restoreUserToken();
    }
    catch (error) {
        console.warn('[algolia-insights] init failed:', error);
    }
}
/**
 * Whether an event may be sent right now.
 *
 * @returns `true` when the library is initialised and consent is granted.
 */
export function canSendInsights() {
    if (!initialised || !state)
        return false;
    try {
        return state.isAllowed();
    }
    catch {
        return false;
    }
}
/**
 * The cookie options the attribution context must be written with.
 *
 * @returns The configured domain, or an empty object before initialisation.
 */
export function contextCookieOptions() {
    return state?.cookieDomain ? { domain: state.cookieDomain } : {};
}
/**
 * The courses index name, for events with no attribution entry to read it from.
 *
 * @returns The configured index, or `undefined` when the app did not supply one.
 */
export function configuredCoursesIndex() {
    return state?.coursesIndex;
}
/**
 * The `search-insights` entry point.
 *
 * Used by the event helpers here and handed to InstantSearch's insights
 * middleware, which would otherwise pull the library off a CDN.
 *
 * @returns The `aa` function.
 */
export function insightsClient() {
    return aa;
}
/**
 * Sets the authenticated user token, so Algolia can stitch together the same
 * user's sessions across devices. Uses the Okta `sub`, which is opaque.
 *
 * Safe to call before {@link initAlgoliaInsights}: the token is persisted and
 * replayed once the library comes up.
 *
 * @param token - The Okta subject identifier.
 */
export function setAlgoliaUserToken(token) {
    if (typeof window === 'undefined' || !token)
        return;
    try {
        window.localStorage.setItem(USER_TOKEN_KEY, token);
        if (initialised) {
            aa('setAuthenticatedUserToken', token);
        }
    }
    catch (error) {
        console.warn('[algolia-insights] could not set the user token:', error);
    }
}
/** Forgets the authenticated user token. Called on logout. */
export function clearAlgoliaUserToken() {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.removeItem(USER_TOKEN_KEY);
        if (initialised) {
            aa('setAuthenticatedUserToken', undefined);
        }
    }
    catch (error) {
        console.warn('[algolia-insights] could not clear the user token:', error);
    }
}
/**
 * Stops tracking and drops what was stored about this user.
 *
 * Called when measurement consent is withdrawn. `search-insights` cannot be
 * un-initialised, so the consent gate is what actually keeps events from being
 * sent; this clears the state we own.
 */
export function disableAlgoliaInsights(options) {
    // Consent can be withdrawn before anything was ever initialised, in which
    // case there is no stored cookie domain and clearing would remove a
    // host-only cookie while leaving the real one alive. The caller passes the
    // domain for exactly that case.
    const fromState = contextCookieOptions();
    clearInsightContext(fromState.domain ? fromState : (options ?? {}));
    clearAlgoliaUserToken();
}
/** Replays a token stored by an earlier page load. */
function restoreUserToken() {
    try {
        const token = window.localStorage.getItem(USER_TOKEN_KEY);
        if (token) {
            aa('setAuthenticatedUserToken', token);
        }
    }
    catch {
        // localStorage can be unavailable (private mode, blocked storage).
    }
}
/**
 * Resets the module. Test-only — production code initialises once per page
 * load.
 */
export function resetInsightsForTests() {
    state = null;
    initialised = false;
}
